import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import Select from 'react-select';

import incident_manager from '../api/incident'
import { loadCauses } from '../api/causes';

const OTHER_CAUSE_OPTION = { value: '__other_cause__', label: 'Other' };
const CAUSE_LABEL_OVERRIDES = {
    "rider's own mistake": "mistake by a rider involved in the crash",
    "other rider's mistake": "mistake by another rider not involved in the crash"
};

const getCauseDisplayLabel = (causeDescription) => {
    const normalizedCauseDescription = String(causeDescription || '')
        .replace(/[\u2018\u2019]/g, "'")
        .trim()
        .toLowerCase();

    return CAUSE_LABEL_OVERRIDES[normalizedCauseDescription] || causeDescription;
};

const IncidentCommentForm = () => {
    const { token } = useParams()

    const [incident, setIncident] = useState(null);
    const [description, setDescription] = useState('');
    const [improvementSuggestion, setImprovementSuggestion] = useState('');
    const [causeOptions, setCauseOptions] = useState([]);
    const [involvedInIncident, setInvolvedInIncident] = useState(true);
    const [selectedCauses, setSelectedCauses] = useState([]);
    const [otherCause, setOtherCause] = useState('');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const raceName = incident?.race_name || '';
    const stageIndex = incident?.stage_race_index ?? incident?.race_stage_index ?? incident?.stage_index ?? incident?.stage?.stage_index;
    const kmToFinish = incident?.km_to_finish ?? incident?.incident_km_from_finish;
    const ridersInvolved = incident?.number_of_riders_involved ?? incident?.incident_number_of_riders;
    const hasOtherCause = involvedInIncident && selectedCauses.some((cause) => cause.value === OTHER_CAUSE_OPTION.value);

    const stageLabel = (stageIndex) => {
        const numericStageIndex = Number(stageIndex);

        if (!Number.isNaN(numericStageIndex) && numericStageIndex > 0) {
            return `Stage ${numericStageIndex}`;
        }

        return 'One day race';
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const causes = involvedInIncident ? selectedCauses.map((cause) => ({
            cause_id: cause.value === OTHER_CAUSE_OPTION.value ? null : cause.value,
            cause_description: cause.cause_description,
            is_other: cause.value === OTHER_CAUSE_OPTION.value
        })) : [];

        const result = await incident_manager.post_rider_incident_comment(token, description, {
            causes,
            otherCause: hasOtherCause ? otherCause : '',
            improvementSuggestion: involvedInIncident ? improvementSuggestion : '',
            involvedInIncident
        });
        if (result) {
            setStatus('success');
            setDescription('');
            setImprovementSuggestion('');
            setSelectedCauses([]);
            setInvolvedInIncident(true);
            setOtherCause('');
        } else {
            setStatus('error');
        }
    };

    useEffect(() => {
        if (!token) window.location.href = '/unauthorized'

        const fetch = async () => {
            const [response, causes] = await Promise.all([
                incident_manager.get_incident_from_token(token),
                loadCauses()
            ]);

            const options = [
                { ...OTHER_CAUSE_OPTION, cause_description: OTHER_CAUSE_OPTION.label },
                ...causes.map((cause) => ({
                    value: cause.cause_id,
                    label: getCauseDisplayLabel(cause.cause_description),
                    cause_description: cause.cause_description
                }))
            ];

            setCauseOptions(options);

            if (response) {
                setIncident(response)
                setFetchError(false);
            } else {
                setFetchError(true);
            }

            setLoading(false);
        }
        fetch();
    }, [token])

    return (
        <Container className="d-flex justify-content-center align-items-center min-vh-100 py-4">
            <Card style={{ width: '100%', maxWidth: '760px' }}>
                <Card.Body>
                    <Card.Title className="text-center mb-4">Incident Comment</Card.Title>

                    {loading && (
                        <div className="d-flex justify-content-center py-5">
                            <Spinner animation="border" role="status" />
                        </div>
                    )}


                    {fetchError && !loading && (
                        <Alert variant="danger">
                            ❌ Unable to load incident data. The token may be invalid, previously used or expired.
                        </Alert>
                    )}

                    {!loading && incident && !showForm && (
                        <Form>
                            <div className="mb-4">
                                <p>
                                    You are receiving this survey because you were involved in an incident during a race. SafeR wants to understand what happened from your point of view and hear any suggestion you have to make racing safer. There are no wrong answers, and the primary purpose of this survey is to learn from the incident, not to determine blame.
                                </p>
                                <p>
                                    Taking part is your choice. You can skip any optional question or stop at any time before submitting your answers. Not answering has no adverse consequence for you.
                                </p>
                                <p>
                                    Please do not give us any medical or health information. We are not asking for it and we do not want it. Do not describe injuries, diagnoses or treatment. If you do, the relevant information will not be used for the survey analysis and will be removed or deleted as soon as reasonably possible. This survey does not replace any medical, emergency or formal reporting channel.
                                </p>
                                <p>
                                    This survey is separate from any formal complaint or disciplinary procedure and is not intended to determine disciplinary responsibility. However, the UCI may need to use or share relevant information where required by applicable law or the UCI Regulations, or where reasonably necessary to address a serious safety or integrity concern.
                                </p>
                                <p>
                                    Your answers will be treated as confidential. They may be accessed only by authorised SafeR Safety Analysts, relevant UCI personnel and service providers who need access for the purposes described below and who are subject to appropriate confidentiality and data-protection obligations. Your identifiable answers will not be routinely shared with your team, other riders, other teams, race organisers, sponsors or the media. Any wider reporting will use aggregated or anonymised information and will not name you, subject to the exceptional circumstances described above.
                                </p>
                                <p>
                                    The UCI is responsible for the personal data collected through this survey. Your answers, together with the limited identification and incident information needed to understand them, will be used only to support the UCI's and SafeR's rider-safety work: analysing incidents, identifying recurring risks, evaluating this pilot project and developing safety recommendations. Where the GDPR applies, this processing is based on the legitimate interest in improving rider safety.
                                </p>
                                <p>
                                    Your identifiable survey response will be stored securely for 24 months after submission, unless a longer period is reasonably required by applicable law or the UCI Regulations. It will then be deleted or anonymised.
                                </p>
                                <p>
                                    Depending on applicable law, you may ask to access, correct or delete your personal data, restrict its processing or object to its use. These rights may be subject to legal limitations. To exercise them, or to ask any question about how your information is handled, write to data.protection@uci.ch.
                                </p>
                            </div>
                            <Form.Group controlId="disclaimerAccepted" className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    checked={disclaimerAccepted}
                                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                    label="I have read the above. I choose to take part voluntarily."
                                    required
                                />
                            </Form.Group>
                            <Button
                                variant="primary"
                                type="button"
                                className="w-100"
                                disabled={!disclaimerAccepted}
                                onClick={() => setShowForm(true)}
                            >
                                Continue
                            </Button>
                        </Form>
                    )}

                    {!loading && incident && showForm && status === 'success' && (
                        <Alert variant="success" className="text-center mb-0">
                            <Alert.Heading>Thank you</Alert.Heading>
                            <p className="mb-0">
                                Your response has been submitted successfully.
                            </p>
                        </Alert>
                    )}

                    {!loading && incident && showForm && status !== 'success' && (
                        <>
                            {status === 'error' && (
                                <Alert variant="danger" onClose={() => setStatus(null)} dismissible>
                                ❌ Failed to submit comment. Please try again.
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group controlId="incidentDate" className="mb-3">
                                    <Form.Label>Date</Form.Label>
                                    <Form.Control type="text" readOnly value={formatDate(incident?.incident_timestamp)} />
                                </Form.Group>
                                <Form.Group controlId="raceName" className="mb-3">
                                    <Form.Label>Race Name</Form.Label>
                                    <Form.Control type="text" readOnly value={raceName} />
                                </Form.Group>
                                <Form.Group controlId="stageNumber" className="mb-3">
                                    <Form.Label>Stage Number</Form.Label>
                                    <Form.Control type="text" readOnly value={stageLabel(stageIndex)} />
                                </Form.Group>
                                <Form.Group controlId="incidentDistance" className="mb-3">
                                    <Form.Label>km to Finish</Form.Label>
                                    <Form.Control type="text" readOnly value={kmToFinish ?? ''} />
                                </Form.Group>
                                <Form.Group controlId="ridersInvolved" className="mb-3">
                                    <Form.Label>Number of Riders Involved</Form.Label>
                                    <Form.Control type="text" readOnly value={ridersInvolved ?? ''} />
                                </Form.Group>
                                <Form.Group controlId="userComment" className="mb-3">
                                    <Form.Label>Crash Description:</Form.Label>
                                    <Form.Text className="text-muted fst-italic d-block mb-2">
                                        Please describe what happened in your own words. Please do not include any medical or health information. Please avoid identifying other people unless this is necessary to understand the incident. Where possible, describe roles rather than names (for example, "a rider ahead of me" or "a race motorbike").
                                    </Form.Text>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="Describe the crash..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group controlId="involvedInIncident" className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        checked={involvedInIncident}
                                        onChange={(e) => {
                                            const checked = e.target.checked;

                                            setInvolvedInIncident(checked);
                                            if (!checked) {
                                                setSelectedCauses([]);
                                                setOtherCause('');
                                                setImprovementSuggestion('');
                                            }
                                        }}
                                        label="Were you involved in the incident?"
                                    />
                                </Form.Group>
                                {involvedInIncident && (
                                    <>
                                        <Form.Group controlId="incidentCauses" className="mb-3">
                                            <Form.Label>Incident Causes</Form.Label>
                                            <Select
                                                isMulti
                                                name="incident-causes"
                                                options={causeOptions}
                                                value={selectedCauses}
                                                onChange={(options) => setSelectedCauses(options || [])}
                                            />
                                        </Form.Group>
                                        {hasOtherCause && (
                                            <Form.Group controlId="otherCause" className="mb-3">
                                                <Form.Label>Other Cause</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Describe the other cause..."
                                                    value={otherCause}
                                                    onChange={(e) => setOtherCause(e.target.value)}
                                                    required={hasOtherCause}
                                                />
                                            </Form.Group>
                                        )}
                                        <Form.Group controlId="improvementSuggestion" className="mb-3">
                                            <Form.Label>What could be improved?</Form.Label>
                                            <Form.Text className="text-muted fst-italic d-block mb-2">
                                                Are there any measures or changes that you think could help prevent or reduce the risk of similar crashes?
                                            </Form.Text>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                placeholder="Share any suggested improvements..."
                                                value={improvementSuggestion}
                                                onChange={(e) => setImprovementSuggestion(e.target.value)}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                                <Button variant="primary" type="submit" className="w-100">Submit Comment</Button>
                            </Form>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
}

export default IncidentCommentForm;
